import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "../api/client";

function BoolBadge({ value, trueLabel = "OK", falseLabel = "Bermasalah" }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
      value ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
    }`}>
      <span className="material-symbols-outlined text-[14px]">{value ? "check_circle" : "error"}</span>
      {value ? trueLabel : falseLabel}
    </span>
  );
}

function InfoRow({ label, value, mono = false }) {
  return (
    <div className="grid grid-cols-1 gap-1 border-b border-gray-100 py-3 last:border-b-0 sm:grid-cols-[220px_1fr]">
      <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className={`text-sm text-gray-900 break-words [overflow-wrap:anywhere] ${mono ? "font-mono" : ""}`}>
        {value === undefined || value === null || value === "" ? "-" : String(value)}
      </dd>
    </div>
  );
}

function Panel({ title, icon, children, action }) {
  return (
    <section className="rounded-lg border border-gray-200 bg-white">
      <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-[20px] text-[#10a37f]">{icon}</span>
          <h2 className="text-sm font-bold text-gray-900">{title}</h2>
        </div>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

export default function QwenVlDebugView() {
  const { showToast } = useOutletContext();
  const [status, setStatus] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [selectedDocumentId, setSelectedDocumentId] = useState("");
  const [validation, setValidation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState("");

  const selectedDocument = useMemo(
    () => documents.find((doc) => doc.document_id === selectedDocumentId),
    [documents, selectedDocumentId]
  );

  async function loadDebugData() {
    setLoading(true);
    setError("");
    try {
      const [nextStatus, nextDocuments] = await Promise.all([
        api.qwenVlDebugStatus(),
        api.qwenVlSampleDocuments("SURAT_EDARAN"),
      ]);
      setStatus(nextStatus);
      setDocuments(nextDocuments);
      setSelectedDocumentId((current) => current || nextDocuments[0]?.document_id || "");
    } catch (err) {
      setError(err.message || "Gagal memuat debug Qwen-VL");
      showToast(err.message || "Gagal memuat debug Qwen-VL", "error");
    } finally {
      setLoading(false);
    }
  }

  async function runValidation() {
    setTesting(true);
    setValidation(null);
    try {
      const result = await api.validateQwenVlSample({
        documentId: selectedDocumentId || undefined,
        collectionCode: "SURAT_EDARAN",
        force: true,
      });
      setValidation(result);
      showToast("Test validasi Qwen-VL selesai.");
    } catch (err) {
      showToast(err.message || "Test validasi Qwen-VL gagal", "error");
    } finally {
      setTesting(false);
    }
  }

  useEffect(() => {
    loadDebugData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const ready = Boolean(status?.ready);
  const lmstudio = status?.lmstudio || {};
  const pdfRenderer = status?.pdf_renderer || {};
  const validationResult = validation?.validation;

  return (
    <main className="flex-1 min-h-0 overflow-y-auto bg-gray-50">
      <header className="sticky top-0 z-30 border-b border-gray-200 bg-white">
        <div className="flex min-h-[68px] flex-col justify-center gap-1 px-5 pl-14 lg:px-7">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-900">
                <span className="material-symbols-outlined text-[20px] text-white">visibility</span>
              </div>
              <div>
                <h1 className="text-lg font-bold leading-tight text-gray-900">Qwen-VL Debug</h1>
                <p className="text-xs text-gray-500">Validasi runtime Qwen-VL 8B untuk Super Admin</p>
              </div>
            </div>
            <button
              type="button"
              onClick={loadDebugData}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60"
            >
              <span className={`material-symbols-outlined text-[18px] ${loading ? "animate-spin" : ""}`}>refresh</span>
              Refresh
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl space-y-5 px-4 py-5 md:px-7">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-4">
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Status</p>
            <div className="mt-3"><BoolBadge value={ready} trueLabel="Ready" falseLabel="Belum Ready" /></div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">PDF Renderer</p>
            <div className="mt-3"><BoolBadge value={pdfRenderer.available} trueLabel="Terpasang" falseLabel="Missing" /></div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">LM Studio</p>
            <div className="mt-3"><BoolBadge value={lmstudio.reachable} trueLabel="Terhubung" falseLabel="Tidak Terhubung" /></div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Model Loaded</p>
            <div className="mt-3"><BoolBadge value={lmstudio.configured_model_loaded} trueLabel="Loaded" falseLabel="Belum Loaded" /></div>
          </div>
        </div>

        <div className="grid gap-5 lg:grid-cols-[1fr_1fr]">
          <Panel title="Runtime Config" icon="tune">
            <dl>
              <InfoRow label="Raw Validation" value={status?.raw_file_validation_enabled ? "enabled" : "disabled"} />
              <InfoRow label="Max Chunks" value={status?.raw_file_validation_max_chunks} />
              <InfoRow label="Require Supported" value={status?.raw_file_validation_require_supported ? "true" : "false"} />
              <InfoRow label="Debug Notes" value={status?.raw_file_validation_debug_notes ? "true" : "false"} />
              <InfoRow label="VLM Provider" value={status?.vlm_provider} />
              <InfoRow label="VLM Model" value={status?.vlm_model} mono />
              <InfoRow label="VLM Base URL" value={status?.vlm_base_url} mono />
              <InfoRow label="VLM Max Tokens" value={status?.vlm_max_tokens} />
              <InfoRow label="PDF Scale" value={status?.vlm_pdf_render_scale} />
              <InfoRow label="PDF Max Pages" value={status?.vlm_pdf_render_max_pages} />
              <InfoRow label="Image Max Side" value={status?.vlm_image_max_side} />
            </dl>
          </Panel>

          <Panel title="Renderer & Model" icon="fact_check">
            <dl>
              <InfoRow label="Renderer" value={pdfRenderer.name} mono />
              <InfoRow label="Renderer Path" value={pdfRenderer.path} mono />
              <InfoRow label="Renderer Version" value={pdfRenderer.version} mono />
              <InfoRow label="Renderer Error" value={pdfRenderer.error} />
              <InfoRow label="LM Studio URL" value={lmstudio.base_url} mono />
              <InfoRow label="Configured Model" value={lmstudio.configured_model} mono />
              <InfoRow label="LM Studio Error" value={lmstudio.error} />
              <InfoRow label="Loaded Models" value={(lmstudio.models || []).join(", ")} mono />
            </dl>
          </Panel>
        </div>

        <Panel
          title="Test Validasi Qwen-VL"
          icon="play_circle"
          action={
            <button
              type="button"
              onClick={runValidation}
              disabled={testing || loading || !documents.length}
              className="inline-flex items-center gap-2 rounded-lg bg-[#10a37f] px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#0e8f70] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span className={`material-symbols-outlined text-[18px] ${testing ? "animate-spin" : ""}`}>
                {testing ? "progress_activity" : "play_arrow"}
              </span>
              Test Sample
            </button>
          }
        >
          <div className="grid gap-4 lg:grid-cols-[360px_1fr]">
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                Dokumen Sample
              </label>
              <select
                value={selectedDocumentId}
                onChange={(event) => setSelectedDocumentId(event.target.value)}
                className="h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none focus:border-[#10a37f]"
              >
                {documents.map((doc) => (
                  <option key={doc.document_id} value={doc.document_id}>
                    {doc.title}
                  </option>
                ))}
              </select>
              {!documents.length && (
                <p className="mt-2 text-xs text-amber-700">Belum ada dokumen PDF aktif/published yang bisa dipakai sample.</p>
              )}
              {selectedDocument && (
                <div className="mt-3 rounded-lg bg-gray-50 p-3 text-xs text-gray-600">
                  <p className="font-semibold text-gray-900">{selectedDocument.source_name || selectedDocument.title}</p>
                  <p className="mt-1 font-mono break-all">{selectedDocument.document_id}</p>
                  <p className="mt-1">Halaman sample: {selectedDocument.page_no || "-"}</p>
                </div>
              )}
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              {!validationResult ? (
                <p className="text-sm text-gray-500">Klik Test Sample untuk memaksa backend render PDF dan mengirim gambar ke Qwen-VL.</p>
              ) : (
                <dl>
                  <InfoRow label="Status" value={validationResult.status} />
                  <InfoRow label="Method" value={validationResult.method} mono />
                  <InfoRow label="Page" value={validationResult.page_no} />
                  <InfoRow label="Notes" value={validationResult.notes} />
                  <InfoRow label="Corrected Text" value={validationResult.corrected_text} />
                </dl>
              )}
            </div>
          </div>
        </Panel>
      </div>
    </main>
  );
}
